import { ProductSmallCardData } from '../../product-card/product-card-display';

interface CartRemovalInfo {
  removedIndex: number | null;
  message: string;
}

/**
 * Resolves the removed item's index and a removal message from a `removed-from-cart` event.
 *
 * @param event - The `removed-from-cart` CustomEvent.
 * @param products - The component's current product list.
 */
export function resolveCartRemoval(event: Event, products: ProductSmallCardData[]): CartRemovalInfo {
  const detail = (event as CustomEvent).detail;
  const removedIndex = products.findIndex(
    (p) =>
      (detail?.isbn !== undefined && p.isbn === detail.isbn) ||
      (detail?.isbn13 !== undefined && p.isbn13 === detail.isbn13)
  );
  const product = removedIndex >= 0 ? products[removedIndex] : undefined;
  const title = product?.title || detail?.title || 'Item';
  return { removedIndex: removedIndex >= 0 ? removedIndex : null, message: `${title} has been removed.` };
}

/**
 * Focuses `element` after appending `message` to its accessible name (name first, then
 * the removal message), then restores the original name.
 * Avoids aria-live vs focus race conditions.
 *
 * @param element - The element about to receive focus.
 * @param message - The message to append after the element's accessible name.
 * @param restoreDelay - Milliseconds before restoring the original name (default 1000ms).
 */
export function announceViaAccessibleName(element: HTMLElement, message: string, restoreDelay = 1000): void {
  const originalLabel = element.getAttribute('aria-label');
  const accessibleName = (originalLabel ?? element.textContent?.trim() ?? '').trim();
  element.setAttribute('aria-label', `${accessibleName}. ${message}`.trim());
  element.focus();

  setTimeout((): void => {
    if (originalLabel !== null) {
      element.setAttribute('aria-label', originalLabel);
    } else {
      element.removeAttribute('aria-label');
    }
  }, restoreDelay);
}

/**
 * Moves focus to the product card at `removedIndex` after a cart item is removed,
 * announcing the removal via `announceViaAccessibleName`. Call after `updateComplete`.
 *
 * @param renderRoot - The render root of the cart component.
 * @param removedIndex - The zero-based list index of the item that was removed.
 * @param remainingCount - The number of product cards still in the list after removal.
 * @param message - The removal announcement to attach to the focus target.
 * @param onEmpty - Called with `message` when no product cards remain.
 */
export function focusCartCardWithAnnouncement(
  renderRoot: HTMLElement | DocumentFragment,
  removedIndex: number,
  remainingCount: number,
  message: string,
  onEmpty: (message: string) => void
): void {
  const cards = Array.from(renderRoot.querySelectorAll<HTMLElement>('mhecomm-product-card-display'));

  if (cards.length === 0) {
    onEmpty(message);
    return;
  }

  const targetCard = cards[Math.min(removedIndex, remainingCount - 1)];
  const firstFocusable = targetCard.shadowRoot?.querySelector<HTMLElement>(
    'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
  );
  const focusTarget = firstFocusable ?? targetCard;
  if (!firstFocusable) {
    targetCard.setAttribute('tabindex', '-1');
  }

  announceViaAccessibleName(focusTarget, message);
}
